import { format } from 'date-fns';
import type { Locale } from 'date-fns';
import { enUS, es, fr } from 'date-fns/locale';

import { Partner } from 'types';
import { TradingOfferType } from 'types/TradingOffer';

import { formatCurrencyDecimals, formatNumber } from 'utils/formats';

import { getPublisherAddressLine } from './getPublisherAddressLine';

const DATE_LOCALES: Record<string, Locale> = {
  es,
  en: enUS,
  fr,
};

const PENDING = '-';

const replaceTag = (html: string, tagTitle: string, value: string): string => {
  const regex = new RegExp(`\\{+${tagTitle}\\}+`, 'g');
  return html.replace(regex, value);
};

export type ApplyEquityMarketAcceptOfferContractPreviewTagsArgs = {
  html: string;
  language: string;
  operationReferencePlaceholder: string;
  offerType: TradingOfferType;
  publisherPartner: Partial<Partner> | null;
  participantPartner: Partial<Partner> | null;
  purchaseQuantity: number;
  sharePrice: number;
  className: string;
};

export const applyEquityMarketAcceptOfferContractPreviewTags = ({
  html,
  language,
  operationReferencePlaceholder,
  offerType,
  publisherPartner,
  participantPartner,
  purchaseQuantity,
  sharePrice,
  className,
}: ApplyEquityMarketAcceptOfferContractPreviewTagsArgs): string => {
  const langKey = language?.split('-')[0] || 'es';
  const dateLocale = DATE_LOCALES[langKey] || es;

  const isSell = offerType === TradingOfferType.SELL;
  const seller = isSell ? publisherPartner : participantPartner;
  const buyer = isSell ? participantPartner : publisherPartner;

  const sellerName = seller?.name?.trim() || PENDING;
  const sellerCif = seller?.cif?.trim() || PENDING;
  const sellerAddress = getPublisherAddressLine(seller) || PENDING;
  const sellerIban = seller?.bankAccountNumber?.trim() || PENDING;
  const sellerAccountHolder = sellerName;

  const buyerName = buyer?.name?.trim() || PENDING;
  const buyerCif = buyer?.cif?.trim() || PENDING;
  const buyerAddress = getPublisherAddressLine(buyer) || PENDING;

  const totalPrice = purchaseQuantity * sharePrice;
  const shareClassLabel = className?.trim() || PENDING;

  let result = html;

  result = replaceTag(result, 'MERCADO_NOMBRE_VENDEDOR', sellerName);
  result = replaceTag(result, 'MERCADO_DNI_VENDEDOR', sellerCif);
  result = replaceTag(result, 'MERCADO_DOMICILIO_VENDEDOR', sellerAddress);
  result = replaceTag(result, 'MERCADO_IBAN_VENDEDOR', sellerIban);
  result = replaceTag(
    result,
    'MERCADO_TITULAR_CUENTA_VENDEDOR',
    sellerAccountHolder
  );

  result = replaceTag(result, 'MERCADO_NOMBRE_COMPRADOR', buyerName);
  result = replaceTag(result, 'MERCADO_DNI_COMPRADOR', buyerCif);
  result = replaceTag(result, 'MERCADO_DOMICILIO_COMPRADOR', buyerAddress);

  result = replaceTag(
    result,
    'MERCADO_NUM_ACCIONES',
    formatNumber(purchaseQuantity) || PENDING
  );
  result = replaceTag(result, 'MERCADO_CLASE_ACCIONES', shareClassLabel);
  result = replaceTag(
    result,
    'MERCADO_PRECIO_ACCION',
    formatCurrencyDecimals(sharePrice) || PENDING
  );
  result = replaceTag(
    result,
    'MERCADO_PRECIO_TOTAL',
    formatCurrencyDecimals(totalPrice) || PENDING
  );

  result = replaceTag(
    result,
    'MERCADO_FECHA',
    format(new Date(), 'PPP', { locale: dateLocale })
  );
  result = replaceTag(result, 'MERCADO_HORA', format(new Date(), 'HH:mm'));
  result = replaceTag(
    result,
    'MERCADO_REFERENCIA_OPERACION',
    operationReferencePlaceholder
  );

  return result;
};
